import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GitMerge, Users } from 'lucide-react';
import { patientService, PatientDoublon } from '../../services/api';

const dateFr = (d: string) => new Date(d).toLocaleDateString('fr-FR');

/** Patients créés en double (même nom, prénoms et téléphone) : on choisit le
 *  dossier à conserver, les autres lui sont rattachés puis désactivés. */
const DoublonsPatients: React.FC = () => {
  const { data: groupes = [], isLoading } = useQuery(
    ['patients-doublons'],
    () => patientService.getDoublons().then((r) => r.data),
  );

  if (isLoading) return <div className="son-skeleton h-24" />;

  if (groupes.length === 0) {
    return (
      <div className="son-empty">
        <div className="son-empty-icon"><Users className="h-6 w-6" aria-hidden /></div>
        <p>Aucun patient en double.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="son-sub">
        {groupes.length} groupe(s) de doublons. Choisissez le dossier à conserver : consultations, factures,
        rendez-vous et documents des autres lui seront rattachés, puis ces doublons seront désactivés.
      </p>
      {groupes.map((g) => <GroupeDoublons key={g.map((p) => p.id).join('-')} patients={g} />)}
    </div>
  );
};

const GroupeDoublons: React.FC<{ patients: PatientDoublon[] }> = ({ patients }) => {
  const queryClient = useQueryClient();
  // Par défaut : le dossier le plus fourni (à égalité, le plus ancien, déjà en tête)
  const poids = (p: PatientDoublon) => p.nb_consultations + p.nb_factures + p.nb_rendez_vous;
  const [garde, setGarde] = useState(
    patients.reduce((a, b) => (poids(b) > poids(a) ? b : a)).id,
  );
  const [erreur, setErreur] = useState('');

  const fusion = useMutation(
    () => patientService.fusionner(garde, patients.filter((p) => p.id !== garde).map((p) => p.id)),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['patients-doublons']);
        queryClient.invalidateQueries(['patients']);
      },
      onError: (e: any) => setErreur(e?.response?.data?.doublons?.[0] || 'La fusion a échoué.'),
    },
  );

  const confirmer = () => {
    const p = patients.find((x) => x.id === garde)!;
    if (window.confirm(
      `Fusionner ${patients.length - 1} doublon(s) dans le dossier n°${p.id} (créé le ${dateFr(p.created_at)}) ?\n` +
      'Cette opération ne peut pas être annulée.',
    )) {
      setErreur('');
      fusion.mutate();
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between gap-4 p-4">
        <div>
          <div className="font-semibold text-gray-800">{patients[0].nom} {patients[0].prenoms}</div>
          <div className="text-sm text-gray-500">{patients[0].telephone}</div>
        </div>
        <button type="button" className="btn btn-primary" onClick={confirmer} disabled={fusion.isLoading}>
          <GitMerge className="h-4 w-4 mr-2" aria-hidden />
          {fusion.isLoading ? 'Fusion…' : 'Fusionner'}
        </button>
      </div>
      {erreur && <div className="px-4 pb-2 text-sm text-red-600" role="alert">{erreur}</div>}
      <div className="son-table-wrap">
        <table className="son-table">
          <thead>
            <tr>
              <th>Conserver</th>
              <th>Dossier</th>
              <th>Né(e) le</th>
              <th>Assurance</th>
              <th>Consultations</th>
              <th>Factures</th>
              <th>RDV</th>
            </tr>
          </thead>
          <tbody>
            {patients.map((p) => (
              <tr key={p.id}>
                <td>
                  <input
                    type="radio"
                    className="text-gray-700"
                    name={`garde-${patients[0].id}`}
                    checked={garde === p.id}
                    onChange={() => setGarde(p.id)}
                    aria-label={`Conserver le dossier n°${p.id}`}
                  />
                </td>
                <td>n°{p.id} · créé le {dateFr(p.created_at)}</td>
                <td>{dateFr(p.date_naissance)}</td>
                <td>{p.assureur_nom || '—'}</td>
                <td>{p.nb_consultations}</td>
                <td>{p.nb_factures}</td>
                <td>{p.nb_rendez_vous}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DoublonsPatients;
